<template>
  <!-- DESKTOP: фонове фото -->
  <section
    class="hidden md:flex relative h-screen flex-col justify-center overflow-hidden"
    @mousemove="onMouseMove"
    @mouseleave="onMouseLeave"
  >
    <div
      class="absolute inset-0 hero-parallax-bg"
      aria-hidden="true"
    />
    <div
      class="relative z-10 text-left lg:px-48 md:px-12 px-4 md:w-2/3 hero-content"
      :style="contentStyle"
    >
      <h1 class="font-pt-serif text-4xl md:text-6xl font-bold mb-20 text-white hero-title">
        {{ t('hero.title') }}
      </h1>
      <p class="font-pt-serif font-normal mb-7 text-white text-base md:text-xl hero-subtitle max-w-md">
        {{ t('hero.subtitle') }}
      </p>
      <div class="font-montserrat flex flex-wrap gap-3 hero-btn">
        <a
          href="#lessons"
          class="px-6 py-3 border-2 border-white rounded-lg text-white font-medium hover:bg-white hover:text-black transition-colors whitespace-nowrap"
        >
          {{ t('hero.goToLessons') }}
        </a>
      </div>
    </div>
  </section>

  <!-- MOBILE: чорний фон, лого + фото автора знизу -->
  <section class="md:hidden flex flex-col bg-black text-white min-h-screen">
    <!-- Верх: лого + текст -->
    <div class="flex flex-col items-center justify-center flex-1 px-6 pt-36 pb-8 text-center"
         style="background: linear-gradient(180deg, #000 60%, transparent 100%)">
      <h1 class="font-pt-serif text-5xl font-bold mb-4 text-white">
        {{ t('hero.title') }}
      </h1>
      <p class="font-pt-serif text-lg text-white/70 leading-relaxed">
        {{ t('hero.subtitle') }}
      </p>
      <a
        href="#lessons"
        class="mt-8 px-8 py-3 border-2 border-white rounded-lg text-white font-montserrat font-medium hover:bg-white hover:text-black transition-colors"
      >
        {{ t('hero.goToLessons') }}
      </a>
    </div>

    <!-- Низ: фото автора на всю ширину -->
    <div class="relative w-full" style="height: 140vw; max-height: 900px">
      <div class="absolute inset-x-0 top-0 h-16 z-10"
           style="background: linear-gradient(180deg, #000 0%, transparent 100%)" />
      <img
        src="/images/author.jpg"
        alt="Віктор Алєксєєнко"
        class="w-full h-full object-cover object-top"
      >
      <div class="absolute inset-x-0 bottom-0 h-16 z-10"
           style="background: linear-gradient(0deg, #000 0%, transparent 100%)" />
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const { t } = useI18n()

const mouse = ref({ x: 0.5, y: 0.5 })
const isHovering = ref(false)

function onMouseMove(e: MouseEvent) {
  const el = e.currentTarget as HTMLElement
  const rect = el.getBoundingClientRect()
  mouse.value = {
    x: (e.clientX - rect.left) / rect.width,
    y: (e.clientY - rect.top) / rect.height,
  }
  isHovering.value = true
}

function onMouseLeave() {
  isHovering.value = false
  mouse.value = { x: 0.5, y: 0.5 }
}

// Текст зміщується за мишею
const contentStyle = computed(() => {
  const dx = (mouse.value.x - 0.5) * 14
  const dy = (mouse.value.y - 0.5) * 8
  return {
    transform: `translate(${dx}px, ${dy}px)`,
    transition: isHovering.value ? 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  }
})
</script>

<style scoped>
/* CSS scroll parallax через background-attachment: fixed */
.hero-parallax-bg {
  background-image: url('/images/hero-bg.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
}

/* Fade-in + slide-up при завантаженні */
.hero-title {
  animation: heroFadeUp 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  text-shadow:
    0 0 40px rgba(255, 210, 80, 0.6),
    0 0 80px rgba(255, 180, 40, 0.35);
}

@keyframes glowPulse {
  0% {
    text-shadow:
      -6px -3px 40px rgba(255, 210, 80, 0.5),
       6px  2px 80px rgba(255, 180, 40, 0.3);
  }
  25% {
    text-shadow:
       4px -4px 45px rgba(255, 225, 90, 0.65),
      -4px  3px 75px rgba(255, 170, 30, 0.35);
  }
  50% {
    text-shadow:
       6px  3px 38px rgba(255, 200, 70, 0.7),
      -6px -2px 85px rgba(255, 185, 45, 0.3);
  }
  75% {
    text-shadow:
      -3px  5px 42px rgba(255, 215, 85, 0.55),
       3px -4px 78px rgba(255, 175, 35, 0.32);
  }
  100% {
    text-shadow:
      -6px -3px 40px rgba(255, 210, 80, 0.5),
       6px  2px 80px rgba(255, 180, 40, 0.3);
  }
}

.hero-subtitle {
  animation: heroFadeUp 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.15s both;
}
.hero-btn {
  animation: heroFadeUp 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s both;
}

@keyframes heroFadeUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
